'use client';

export const SEEN_FEED_IDS_TTL_MS = 60 * 60 * 1000;

export type SeenFeedIds = {
  policyIds: number[];
  hometownNewsIds: number[];
  homeDonationBusinessIds: number[];
  productNumbers: string[];
  localMarketProducts: string[];
  bestContentsIdxs: string[];
  policyCardIds: number[];
  tourFestivalIds: string[];
};

export type FeedContent = {
  feedContentType?: string;
  contentId?: unknown;
  contentIdx?: unknown;
  productList?: readonly { productId?: unknown }[];
  customPolicyList?: readonly { contentId?: unknown }[];
  tourFestivalList?: readonly { id?: unknown }[];
};

type SeenFeedIdsInput = Partial<Record<keyof SeenFeedIds, unknown>>;

const SEEN_FEED_IDS_EMPTY: SeenFeedIds = {
  policyIds: [],
  hometownNewsIds: [],
  homeDonationBusinessIds: [],
  productNumbers: [],
  localMarketProducts: [],
  bestContentsIdxs: [],
  policyCardIds: [],
  tourFestivalIds: [],
};

const createEmptySeenFeedIds = (): SeenFeedIds => ({
  policyIds: [],
  hometownNewsIds: [],
  homeDonationBusinessIds: [],
  productNumbers: [],
  localMarketProducts: [],
  bestContentsIdxs: [],
  policyCardIds: [],
  tourFestivalIds: [],
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toPositiveInteger = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) {
    return value;
  }

  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;

  const parsed = Number(trimmed);

  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const toStringId = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();

    return trimmed || undefined;
  }

  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) {
    return String(value);
  }
};

const unique = <T>(values: T[]): T[] => [...new Set(values)];

const normalizeNumberIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) return [];

  return unique(
    value.map(toPositiveInteger).filter((id): id is number => id !== undefined),
  );
};

const normalizeStringIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return unique(
    value.map(toStringId).filter((id): id is string => id !== undefined),
  );
};

const removeStorageItem = (storage: Storage, key: string) => {
  try {
    storage.removeItem(key);
  } catch {
    // ignore storage errors
  }
};

export function normalizeSeenFeedIds(value: unknown): SeenFeedIds | undefined {
  if (!isRecord(value)) return undefined;

  const input = value as SeenFeedIdsInput;

  return {
    policyIds: normalizeNumberIds(input.policyIds),
    hometownNewsIds: normalizeNumberIds(input.hometownNewsIds),
    homeDonationBusinessIds: normalizeNumberIds(input.homeDonationBusinessIds),
    productNumbers: normalizeStringIds(input.productNumbers),
    localMarketProducts: normalizeStringIds(input.localMarketProducts),
    bestContentsIdxs: normalizeStringIds(input.bestContentsIdxs),
    policyCardIds: normalizeNumberIds(input.policyCardIds),
    tourFestivalIds: normalizeStringIds(input.tourFestivalIds),
  };
}

export function loadSeenFeedIdsFromStorage(
  storage: Storage,
  key: string,
): SeenFeedIds | undefined {
  try {
    const raw = storage.getItem(key);
    if (!raw) return undefined;

    const parsed: unknown = JSON.parse(raw);
    if (
      !isRecord(parsed) ||
      typeof parsed.savedAt !== 'number' ||
      Date.now() - parsed.savedAt > SEEN_FEED_IDS_TTL_MS
    ) {
      removeStorageItem(storage, key);

      return undefined;
    }

    const normalized = normalizeSeenFeedIds(parsed.data);
    if (!normalized) removeStorageItem(storage, key);

    return normalized;
  } catch {
    removeStorageItem(storage, key);

    return undefined;
  }
}

export function saveSeenFeedIdsToStorage(
  storage: Storage,
  key: string,
  ids: SeenFeedIds,
) {
  try {
    storage.setItem(
      key,
      JSON.stringify({
        savedAt: Date.now(),
        data: normalizeSeenFeedIds(ids) ?? SEEN_FEED_IDS_EMPTY,
      }),
    );
  } catch {
    // ignore storage errors
  }
}

export function loadSeenFeedIds(key: string): SeenFeedIds {
  return (
    loadSeenFeedIdsFromStorage(localStorage, key) ?? createEmptySeenFeedIds()
  );
}

const lastSeenIdsSnapshot = new Map<string, SeenFeedIds>();

// 뒤로가기/앞으로가기로 복원된 진입인지 판별한다.
// Next App Router는 새 push마다 history.state를 자체 상태로 교체하므로
// HistoryObserver가 replaceState로 심은 historyIndex가 사라진다(= 새 진입).
// popstate 복원 시에는 해당 entry의 historyIndex가 그대로 살아있다(= 뒤로가기).
function isHistoryRestoreNavigation(): boolean {
  if (typeof window === 'undefined') return false;

  const historyState: { historyIndex?: number } = window.history.state ?? {};

  return typeof historyState.historyIndex === 'number';
}

// queryKey에 들어가는 "이미 본 ID" 스냅샷을 결정한다.
// 뒤로가기로 돌아왔을 땐 직전 스냅샷을 그대로 재사용해 queryKey를 고정 →
// 캐시(불러온 페이지 전체)를 복원하고 스크롤을 유지한다.
// 그 외 새 진입(메뉴·링크 클릭 등)에서는 현재까지 누적된 본 ID로 새 스냅샷을
// 만들어 새 피드로 새로고침한다.
export function getInitialSeenIds(
  scopeKey: string,
  storageKey: string,
): SeenFeedIds {
  const cached = lastSeenIdsSnapshot.get(scopeKey);
  if (cached && isHistoryRestoreNavigation()) return cached;

  const loaded = loadSeenFeedIds(storageKey);
  lastSeenIdsSnapshot.set(scopeKey, loaded);

  return loaded;
}

export function saveSeenFeedIds(key: string, ids: SeenFeedIds) {
  saveSeenFeedIdsToStorage(localStorage, key, ids);
}

export function extractSeenIds(contents: readonly FeedContent[]): SeenFeedIds {
  const seenIds = createEmptySeenFeedIds();

  contents.forEach((content) => {
    if (content.feedContentType === 'POLICY') {
      seenIds.policyIds.push(...normalizeNumberIds([content.contentId]));
    }

    if (content.feedContentType === 'CUSTOM_POLICY') {
      seenIds.policyIds.push(...normalizeNumberIds([content.contentId]));
    }

    if (content.feedContentType === 'HOMETOWN_NEWS') {
      seenIds.hometownNewsIds.push(...normalizeNumberIds([content.contentId]));
    }

    if (content.feedContentType === 'DONATION') {
      seenIds.homeDonationBusinessIds.push(
        ...normalizeNumberIds([content.contentId]),
      );
    }

    if (content.feedContentType === 'HOME_PRODUCT') {
      seenIds.productNumbers.push(
        ...normalizeStringIds(
          content.productList?.map((product) => product.productId),
        ),
      );
    }

    if (content.feedContentType === 'LOCAL_MARKET') {
      seenIds.localMarketProducts.push(
        ...normalizeStringIds(
          content.productList?.map((product) => product.productId),
        ),
      );
    }

    if (content.feedContentType === 'BEST_CONTENTS') {
      seenIds.bestContentsIdxs.push(
        ...normalizeStringIds([content.contentIdx]),
      );
    }

    if (content.feedContentType === 'POLICY_CARD') {
      seenIds.policyCardIds.push(...normalizeNumberIds([content.contentId]));
    }

    if (content.feedContentType === 'THEME') {
      seenIds.tourFestivalIds.push(
        ...normalizeStringIds(
          content.tourFestivalList?.map((festival) => festival.id),
        ),
      );
    }

    seenIds.policyIds.push(
      ...normalizeNumberIds(
        content.customPolicyList?.map((policy) => policy.contentId),
      ),
    );
  });

  return normalizeSeenFeedIds(seenIds) ?? createEmptySeenFeedIds();
}

export function mergeSeenIds(...seenIdsList: SeenFeedIds[]): SeenFeedIds {
  const merged = createEmptySeenFeedIds();

  seenIdsList.forEach((seenIds) => {
    merged.policyIds.push(...seenIds.policyIds);
    merged.hometownNewsIds.push(...seenIds.hometownNewsIds);
    merged.homeDonationBusinessIds.push(...seenIds.homeDonationBusinessIds);
    merged.productNumbers.push(...seenIds.productNumbers);
    merged.localMarketProducts.push(...seenIds.localMarketProducts);
    merged.bestContentsIdxs.push(...seenIds.bestContentsIdxs);
    merged.policyCardIds.push(...seenIds.policyCardIds);
    merged.tourFestivalIds.push(...seenIds.tourFestivalIds);
  });

  return normalizeSeenFeedIds(merged) ?? createEmptySeenFeedIds();
}

export function toBodyFilter(ids: SeenFeedIds): Partial<SeenFeedIds> {
  const normalized = normalizeSeenFeedIds(ids) ?? createEmptySeenFeedIds();

  return {
    ...(normalized.policyIds.length > 0 && {
      policyIds: normalized.policyIds,
    }),
    ...(normalized.hometownNewsIds.length > 0 && {
      hometownNewsIds: normalized.hometownNewsIds,
    }),
    ...(normalized.homeDonationBusinessIds.length > 0 && {
      homeDonationBusinessIds: normalized.homeDonationBusinessIds,
    }),
    ...(normalized.productNumbers.length > 0 && {
      productNumbers: normalized.productNumbers,
    }),
    ...(normalized.localMarketProducts.length > 0 && {
      localMarketProducts: normalized.localMarketProducts,
    }),
    ...(normalized.bestContentsIdxs.length > 0 && {
      bestContentsIdxs: normalized.bestContentsIdxs,
    }),
    ...(normalized.policyCardIds.length > 0 && {
      policyCardIds: normalized.policyCardIds,
    }),
    ...(normalized.tourFestivalIds.length > 0 && {
      tourFestivalIds: normalized.tourFestivalIds,
    }),
  };
}
